import { useEffect, useRef } from "react";

export interface RealtimeEvent {
  type: string;
  id: string;
  // Stage-level events (e.g. download:progress) carry extras
  stage?: string;
  job_id?: string;
  error_message?: string;
  [key: string]: unknown;
}

type EventHandler = (event: RealtimeEvent) => void;

// ── Singleton WebSocket state ─────────────────────────────────────────────────
// One shared connection for the whole app, reconnects automatically.

const listeners = new Map<string, Set<EventHandler>>();
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 2000; // ms, grows on repeated failures

function connect() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const url = `${protocol}://${window.location.host}/api/events`;

  ws = new WebSocket(url);

  ws.onmessage = (e) => {
    try {
      const event = JSON.parse(e.data as string) as RealtimeEvent;
      dispatch(event.type, event);
      dispatch("*", event);
    } catch {
      // ignore malformed frames
    }
  };

  ws.onopen = () => {
    reconnectDelay = 2000; // reset backoff on successful connect
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  };

  ws.onclose = () => {
    ws = null;
    // Reconnect with capped exponential backoff (max 30s)
    reconnectTimer = setTimeout(() => {
      reconnectDelay = Math.min(reconnectDelay * 1.5, 30_000);
      connect();
    }, reconnectDelay);
  };

  ws.onerror = () => {
    ws?.close(); // triggers onclose → reconnect
  };
}

function dispatch(type: string, event: RealtimeEvent) {
  const handlers = listeners.get(type);
  if (handlers) handlers.forEach((h) => h(event));
}

function addListener(type: string, handler: EventHandler): () => void {
  if (!listeners.has(type)) listeners.set(type, new Set());
  listeners.get(type)!.add(handler);

  // Ensure a connection is open whenever there are active listeners
  connect();

  return () => {
    const set = listeners.get(type);
    if (set) {
      set.delete(handler);
      if (set.size === 0) listeners.delete(type);
    }
  };
}

/**
 * Returns true when the user is actively typing in an input, textarea, select,
 * or contenteditable. Use this to skip background data-refreshes that would
 * clobber unsaved edits.
 */
export function isEditingField(): boolean {
  const ae = document.activeElement as HTMLElement | null;
  if (!ae) return false;
  const tag = ae.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || ae.isContentEditable;
}

/**
 * Subscribe to real-time events from the server.
 *
 * @param type     Event type to listen for, e.g. "song:updated", "setlist:updated".
 *                 Pass "*" to receive all events.
 * @param handler  Called each time a matching event arrives.
 *
 * The handler ref is kept stable so callers don't need to memoize it —
 * always calls the latest version of the function.
 */
export function useRealtime(type: string, handler: EventHandler): void {
  const handlerRef = useRef<EventHandler>(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const stable: EventHandler = (e) => handlerRef.current(e);
    return addListener(type, stable);
  }, [type]);
}
